import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Socket from '../../../connection/socket';
import type { TeamMember } from '../../../stores/types/home';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import classNames from 'classnames';
import Avatar from '../../general-components/avatar';
import LoadingSpinner from '../../general-components/loading-spinner';
import { showAlert } from '../../general-components/alert-service';
import { GreenroomState } from '../../../stores/types/types';
import useHome from '../../../hooks/use-home';


function sortNameAsc(memberA: TeamMember, memberB: TeamMember) {
	var nameA = memberA.first_name?.toUpperCase() + memberA.last_name?.toUpperCase();
	var nameB = memberB.first_name?.toUpperCase() + memberB.last_name?.toUpperCase();
	if (nameA > nameB) return -1;
	if (nameA < nameB) return 1;
	return 0;
}

function sortNameDesc(memberA: TeamMember, memberB: TeamMember) {
	var nameA = memberA.first_name?.toUpperCase() + memberA.last_name?.toUpperCase();
	var nameB = memberB.first_name?.toUpperCase() + memberB.last_name?.toUpperCase();
	if (nameA < nameB) return -1;
	if (nameA > nameB) return 1;
	return 0;
}

function sortEmailAsc(memberA: TeamMember, memberB: TeamMember) {
	var nameA = memberA.email?.toUpperCase();
	var nameB = memberB.email?.toUpperCase();
	if (nameA > nameB) return -1;
	if (nameA < nameB) return 1;
	return 0;
}

function sortEmailDesc(memberA: TeamMember, memberB: TeamMember) {
	var nameA = memberA.email?.toUpperCase();
	var nameB = memberB.email?.toUpperCase();
	if (nameA < nameB) return -1;
	if (nameA > nameB) return 1;
	return 0;
}

function sortRoleAsc(memberA: TeamMember, memberB: TeamMember) {
	if (memberA.isProducer > memberB.isProducer) return -1;
	if (memberA.isProducer < memberB.isProducer) return 1;
	return 0;
}

function sortRoleDesc(memberA: TeamMember, memberB: TeamMember) {
	if (memberA.isProducer < memberB.isProducer) return -1;
	if (memberA.isProducer > memberB.isProducer) return 1;
	return 0;
}

function filterIsProducer(member: TeamMember) {
	return member.isProducer;
}

interface Props {
	socket: Socket | null;
}

export default function Teams(props: Props) {
	const teamsLoaded = useSelector((state: GreenroomState) => state.HomeReducer.teamsLoaded);
	const { teamForSelectedChannel } = useHome();
	const [searchString, setSearchString] = useState('');
	const [sortType, setSortType] = useState('name-desc')

	const filteredMembers = useMemo((): TeamMember[] => {
		//I don't see search debounce needed, not a lot going on processer-wise when we're on this screen
		const producersOnChannel = teamForSelectedChannel.teamMembers.filter(filterIsProducer)
		const formattedSearchString = searchString.toLowerCase().trim()
		return producersOnChannel.filter((member: TeamMember) => {
			if (member.first_name?.toLowerCase().trim().includes(formattedSearchString)
				|| member.last_name?.toLowerCase().trim().includes(formattedSearchString)
				|| member.email?.toLowerCase().trim().includes(formattedSearchString)) {
				return 1;
			} else {
				return 0;
			}
		});
	}, [teamForSelectedChannel, searchString])

	const filteredAndSortedMembers = useMemo((): TeamMember[] => {
		if (sortType === 'name-desc') return filteredMembers.sort(sortNameDesc)
		if (sortType === 'name-asc') return filteredMembers.sort(sortNameAsc)
		if (sortType === 'email-desc') return filteredMembers.sort(sortEmailDesc)
		if (sortType === 'email-asc') return filteredMembers.sort(sortEmailAsc)
		if (sortType === 'role-desc') return filteredMembers.sort(sortRoleDesc)
		if (sortType === 'role-asc') return filteredMembers.sort(sortRoleAsc)
		return filteredMembers
	}, [filteredMembers, sortType])

	const sortName = () => {
		if (sortType === 'name-asc') return setSortType('name-desc')
		if (sortType === 'name-desc') return setSortType('name-asc')
		return setSortType('name-desc')
	}

	const sortEmail = () => {
		if (sortType === 'email-asc') return setSortType('email-desc')
		if (sortType === 'email-desc') return setSortType('email-asc')
		return setSortType('email-desc')
	}

	const sortRole = () => {
		if (sortType === 'role-asc') return setSortType('role-desc')
		if (sortType === 'role-desc') return setSortType('role-asc')
		return setSortType('role-desc')
	}

	const showInviteAlert = () => {
		showAlert({
			message: "Ask your Customer Success Manager to add an additional Producer.",
			description: "",
			type: 'neutral',
			duration: 5000
		});
	}

	return (
		!teamsLoaded
			? <div className="team-view margin-large"><LoadingSpinner /></div>
			: <div className="team-view margin-large">
				<div className="justify-content-space-between margin-bottom-large">
					<h1 className="text-light-gray font-weight-bold margin-none">Team</h1>
					<div className="flex-align-center">
						<div className="search-field-teams">
							<input
								type="text"
								placeholder={"Search"}
								onChange={e => setSearchString(e.target.value)}
								value={searchString} />
							<span className="search-icon">
								<Icon name={ICONS.SEARCH} color={COLORS.DEFAULT_GRAY} size={14}></Icon>
							</span>
						</div>
						<div
							onClick={showInviteAlert}
							className="padding-button-small corner-round-massive background-green margin-left-medium align-items-center button-background-shading cursor-pointer line-height-16"
						>
							<Icon name={ICONS.INVITE} color={COLORS.BLACK} size={12}></Icon>
							<span className="margin-left-small font-weight-bold text-dark">Invite</span>
						</div>
					</div>
				</div>
				<div className="team-row border-none justify-content-space-between text-gray margin-bottom-medium">
					<div className="flex flex-1 align-items-center">
						<span onClick={sortName}>NAME</span>
						<div onClick={sortName} className="flex-column margin-left-small">
							<span className={classNames('arrow', { 'active': sortType === 'name-asc' })}>{"\u25B2"}</span>
							<span className={classNames('arrow', { 'active': sortType === 'name-desc' })}>{"\u25BC"}</span>
						</div>
					</div>
					<div className="flex flex-1 align-items-center">
						<span onClick={sortEmail}>EMAIL</span>
						<div onClick={sortEmail} className="flex-column margin-left-small">
							<span className={classNames('arrow', { 'active': sortType === 'email-asc' })}>{"\u25B2"}</span>
							<span className={classNames('arrow', { 'active': sortType === 'email-desc' })}>{"\u25BC"}</span>
						</div>
					</div>
					<div className="flex flex-1 align-items-center">
						<span onClick={sortRole}>ROLE</span>
						<div onClick={sortRole} className="flex-column margin-left-small">
							<span className={classNames('arrow', { 'active': sortType === 'role-asc' })}>{"\u25B2"}</span>
							<span className={classNames('arrow', { 'active': sortType === 'role-desc' })}>{"\u25BC"}</span>
						</div>
					</div>
				</div>
				{filteredAndSortedMembers.map((teamMember: TeamMember, index: number) => {
					return (
						<div key={index} className="team-row padding-top-medium padding-bottom-medium justify-content-space-between">
							<div className="flex-1 align-items-center">
								<Avatar user={teamMember} size={'thirtytwo'} />
								&nbsp;&nbsp;{teamMember.first_name}
								&nbsp;{teamMember.last_name}
							</div>
							<div className="flex-1 align-items-center">{teamMember.email}</div>
							<div className="flex-1 align-items-center">
								{teamMember.isProducer ? "Producer" : "Member"}
							</div>
						</div>
					)
				})}
			</div>
	)
}